import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faPrint } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { ChangeEvent, FunctionComponent } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Period } from '@spike/model';
import { Tabs } from 'components/UI/V2/Tabs/Tabs';
import { useMediaQuery } from 'react-responsive';
import SearchComponentPopup from 'components/UI/SearchComponentPopup';
import Button from 'components/UI/V3/Button/Button';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import TasksTitlePortal from '../TasksTitlePortal';
import RoomsFilter from './filters/RoomsFilter';
import StaffFilter from './filters/StaffFilter';
import OpsStatusTabsSelect from './OpsStatusTabsSelect';
import { TasksDatePicker } from '../TasksDatePicker';
import styles from './OpsTasksHeader.module.scss';

export interface OpsTab {
    id: string;
    name: string;
    status?: string;
}

export const OPS_TABS: Array<OpsTab> = [
    { id: 'todo', name: 'To-Do', status: 'todo' },
    { id: 'in_progress', name: 'In Progress', status: 'in_progress' },
    { id: 'past_due', name: 'Past Due', status: 'past_due' },
    { id: 'completed', name: 'Completed', status: 'completed' },
    { id: 'all', name: 'All', status: undefined }
];

export interface OpsTasksPendingCount {
    todoCount: number;
    inProgressCount: number;
    pastDueCount: number;
    completedCount: number;
    totalCount: number;
}

interface OpsTasksHeaderProps {
    search: string;
    className?: string;
    selectedTabId: string;
    pendingCount?: OpsTasksPendingCount;
    datePeriod: Period & { name?: string };
    selectedMarketplaceIds: Array<number>;
    selectedRoomIds: Array<number>;
    selectedStaffIds: Array<number>;
    onSearch?: (search: string) => void;
    onChangeTab?: (tabId: string) => void;
    onChangeDate?: (period: Period & { name?: string }) => void;
    onChangeMarketplaces?: (marketplaceIds: Array<number>) => void;
    onChangeRooms?: (roomIds: Array<number>) => void;
    onChangeStaff?: (staffIds: Array<number>) => void;
    onAdd?: () => void;
    onPrint?: () => void;
    showCompleteAll?: boolean;
    onCompleteAllClick?: () => void;
}

export const OpsTasksHeader: FunctionComponent<OpsTasksHeaderProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:960px)' });
    const debounced = useDebouncedCallback(value => props.onSearch?.(value), 500);
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const showMarketplaceFilter = organizationMarketplaces.length >= 2;
    const defaultMarketplaceIds = organizationMarketplaces
        .map(marketplace => marketplace.id)
        .filter((id): id is number => id !== undefined);

    const changeSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
        debounced(event.target.value);
    };

    const calendarFilter = (
        <TasksDatePicker
            value={props.datePeriod}
            className={styles.datePicker}
            onChange={period => props.onChangeDate?.(period)}
        />
    );

    const effectiveMarketplaceIds =
        props.selectedMarketplaceIds.length > 0 ? props.selectedMarketplaceIds : defaultMarketplaceIds;

    const tabCount = (tabId: string): number => {
        if (!props.pendingCount) return 0;
        switch (tabId) {
            case 'todo':
                return props.pendingCount.todoCount;
            case 'in_progress':
                return props.pendingCount.inProgressCount;
            case 'past_due':
                return props.pendingCount.pastDueCount;
            case 'completed':
                return props.pendingCount.completedCount;
            case 'all':
                return props.pendingCount.totalCount;
            default:
                return 0;
        }
    };

    const statusTabs = (
        <Tabs
            value={props.selectedTabId}
            tabs={OPS_TABS.map(tab => ({
                id: tab.id,
                name: tab.name,
                count: tabCount(tab.id)
            }))}
            onChange={tabId => props.onChangeTab?.(tabId)}
        />
    );

    const locationsFilter = showMarketplaceFilter && (
        <OrganizationMarketplacesFilter
            selected={effectiveMarketplaceIds}
            onChangeSelection={ids => props.onChangeMarketplaces?.(ids)}
        />
    );

    const roomsFilter = (
        <RoomsFilter
            selected={props.selectedRoomIds}
            onChangeSelection={ids => props.onChangeRooms?.(ids)}
        />
    );

    const staffFilter = (
        <StaffFilter
            selected={props.selectedStaffIds}
            onChangeSelection={ids => props.onChangeStaff?.(ids)}
        />
    );

    const printButton = (
        <Button
            label=""
            type="button"
            size="large"
            onlyIcon
            leftIcon={faPrint}
            variant="black"
            onClick={() => props.onPrint?.()}
        />
    );

    const addButton = (
        <Button
            label="Add"
            type="button"
            size={isMobile ? 'medium' : 'large'}
            variant="primary"
            className={isMobile ? styles.addButtonMobile : undefined}
            onClick={() => props.onAdd?.()}
        />
    );

    const mobileHeader = (
        <div className={clsx(styles.container, props.className)}>
            <TasksTitlePortal>
                <SearchComponentPopup
                    flushIcon
                    title="Tasks"
                    placeholder="Search by task"
                    onSearch={text => debounced(text)}
                />
                {addButton}
            </TasksTitlePortal>

            <div className={styles.row}>
                <OpsStatusTabsSelect
                    value={props.selectedTabId}
                    options={OPS_TABS.map(tab => ({ id: tab.id, name: tab.name, count: tabCount(tab.id) }))}
                    onChange={tabId => props.onChangeTab?.(tabId)}
                />
                {roomsFilter}
            </div>

            <div className={styles.row}>
                <div className={styles.mobileLeft}>
                    {calendarFilter}
                    {locationsFilter}
                    {staffFilter}
                    {printButton}
                </div>
                {props.showCompleteAll && (
                    <Button
                        label="Complete All"
                        variant="outlined"
                        size="small"
                        className={styles.completeAllButton}
                        onClick={() => props.onCompleteAllClick?.()}
                    />
                )}
            </div>
        </div>
    );

    const notMobileHeader = (
        <div className={clsx(styles.container, props.className)}>
            <div className={styles.row}>
                <div className={styles.left}>
                    <Input
                        value={props.search}
                        startIcon={<FontAwesomeIcon icon={faSearch} />}
                        placeholder="Search by task"
                        className={styles.search}
                        onChange={changeSearchHandler}
                    />
                </div>
                <div className={styles.right}>
                    {locationsFilter}
                    {calendarFilter}
                    {roomsFilter}
                    {staffFilter}
                    {printButton}
                    {addButton}
                </div>
            </div>
            <div className={styles.row}>{statusTabs}</div>
        </div>
    );

    return <>{isMobile ? mobileHeader : notMobileHeader}</>;
};

export default OpsTasksHeader;
